/**
 * Complaint dashboard — status mix, SLA performance and category/product trends.
 * Rebuilt on the Tailwind design system (PageHeader, count-up StatCards, ui/Card,
 * skeletons). KPI values render in text-content (legible) with the accent kept on
 * the border/icon — fixing the low-contrast gold/green value text. Queries, chart
 * series, navigation unchanged.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { AlertTriangle, CheckCircle2, Clock, Wrench } from 'lucide-react';
import { request } from '../../api/client';
import { themedCatAxis, themedValAxis, themedLegendText, themedLabel } from '../../lib/chartTheme';
import { PageHeader, StatCard, Card, Alert, SkeletonStats, Skeleton, theme } from '../../ui';
import { CountUp } from '../../ui/motion';

const STATUS_COLOR: Record<string, string> = {
  OPEN: '#1677FF', IN_PROGRESS: '#E7B22C', ESCALATED: '#D9363E',
  RESOLVED: '#339022', CLOSED: '#8c8c8c', REOPENED: '#FF7A45',
};

export function ComplaintDashboard() {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const { data, isLoading, isError } = useQuery({ queryKey: ['dashboard-complaint'], queryFn: () => request<any>({ url: '/dashboard/complaint' }) });
  const { data: byCategory } = useQuery({ queryKey: ['complaint-trends-cat'], queryFn: () => request<any[]>({ url: '/ai/complaints/trends/category', params: { days: 30 } }) });
  const { data: byProduct } = useQuery({ queryKey: ['complaint-trends-prod'], queryFn: () => request<any[]>({ url: '/ai/complaints/trends/product', params: { days: 30 } }) });
  const { data: slaPerf } = useQuery({ queryKey: ['complaint-sla'], queryFn: () => request<any[]>({ url: '/ai/complaints/trends/sla', params: { days: 30 } }) });

  const header = <PageHeader title="Complaint dashboard" subtitle="Status mix, SLA performance and 30-day category / product trends." icon={<Wrench />} />;

  if (isError) return <div>{header}<Alert type="error" title="Couldn't load the complaint dashboard" description="Please retry in a moment." /></div>;
  if (isLoading) {
    return (
      <div>
        {header}
        <SkeletonStats count={4} className="mb-4" />
        <div className="grid gap-4 md:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-card" />)}</div>
      </div>
    );
  }

  const summary = data?.summary ?? [];
  const open = summary.find((s: any) => s.status === 'OPEN')?.count ?? 0;
  const inProgress = summary.find((s: any) => s.status === 'IN_PROGRESS')?.count ?? 0;
  const resolved = summary.find((s: any) => s.status === 'RESOLVED')?.count ?? 0;
  const slaBreach = data?.slaBreach ?? 0;

  const statusOption = {
    tooltip: { trigger: 'item' },
    legend: { orient: 'vertical', right: 0, top: 'middle', textStyle: themedLegendText(token) },
    series: [{
      type: 'pie', radius: ['42%', '70%'], itemStyle: { borderColor: token.colorBgContainer, borderWidth: 2 },
      data: summary.map((s: any) => ({ name: s.status, value: Number(s.count), itemStyle: { color: STATUS_COLOR[s.status] ?? '#8c8c8c' } })),
      label: { formatter: '{b}\n{c}', ...themedLabel(token) },
    }],
  };
  const categoryOption = {
    grid: { left: 100, right: 20, top: 30, bottom: 40 },
    xAxis: { type: 'value', ...themedValAxis(token) },
    yAxis: { type: 'category', data: (byCategory ?? []).slice(0, 10).map((c: any) => `${c.category} (${c.severity})`).reverse(), ...themedCatAxis(token) },
    tooltip: { trigger: 'axis' },
    series: [{ type: 'bar', barMaxWidth: 16, itemStyle: { color: '#E7B22C', borderRadius: [0, 3, 3, 0] }, data: (byCategory ?? []).slice(0, 10).map((c: any) => Number(c.total)).reverse() }],
  };
  const productOption = {
    grid: { left: 100, right: 20, top: 30, bottom: 40 },
    xAxis: { type: 'value', ...themedValAxis(token) },
    yAxis: { type: 'category', data: (byProduct ?? []).slice(0, 10).map((p: any) => p.sku).reverse(), ...themedCatAxis(token) },
    tooltip: { trigger: 'axis' },
    series: [{ type: 'bar', barMaxWidth: 16, itemStyle: { color: '#D9363E', borderRadius: [0, 3, 3, 0] }, data: (byProduct ?? []).slice(0, 10).map((p: any) => Number(p.total)).reverse() }],
  };
  const slaOption = {
    grid: { left: 60, right: 20, top: 30, bottom: 40 },
    legend: { top: 0, textStyle: themedLegendText(token) },
    xAxis: { type: 'category', data: (slaPerf ?? []).map((s: any) => s.severity), ...themedCatAxis(token) },
    yAxis: { type: 'value', ...themedValAxis(token) },
    tooltip: { trigger: 'axis' },
    series: [
      { name: 'Met', type: 'bar', stack: 'sla', data: (slaPerf ?? []).map((s: any) => Number(s.met)), itemStyle: { color: '#339022' } },
      { name: 'Breached', type: 'bar', stack: 'sla', data: (slaPerf ?? []).map((s: any) => Number(s.breached)), itemStyle: { color: '#D9363E' } },
      { name: 'Open', type: 'bar', stack: 'sla', data: (slaPerf ?? []).map((s: any) => Number(s.open_count)), itemStyle: { color: '#1677FF' } },
    ],
  };

  return (
    <div>
      {header}

      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Open" tone="sky" icon={<AlertTriangle />} onClick={() => navigate('/complaints')} value={<CountUp value={open} />} />
        <StatCard label="In progress" tone="warning" icon={<Clock />} onClick={() => navigate('/complaints')} value={<CountUp value={inProgress} />} />
        <StatCard label="SLA breach" tone="danger" icon={<AlertTriangle />} onClick={() => navigate('/complaints')} value={<CountUp value={slaBreach} />} hint={slaBreach > 0 ? 'Needs attention' : 'On track'} />
        <StatCard label="Resolved" tone="success" icon={<CheckCircle2 />} onClick={() => navigate('/complaints')} value={<CountUp value={resolved} />} />
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_1.4fr]">
        <Card title="By status"><ReactECharts option={statusOption} style={{ height: 180 }} /></Card>
        <Card title="SLA performance by severity"><ReactECharts option={slaOption} style={{ height: 180 }} /></Card>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card title="By category & severity" extra="Last 30 days"><ReactECharts option={categoryOption} style={{ height: 180 }} /></Card>
        <Card title="By product" extra="Click bar → complaints">
          <ReactECharts option={productOption} style={{ height: 180 }} onEvents={{ click: (p: any) => p?.name && navigate('/complaints') }} />
        </Card>
      </div>
    </div>
  );
}
